import React from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';

function CountRow({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testId}>
      <span className="text-zinc-400">{label}</span>
      <span className="font-medium text-white">{value ?? 0}</span>
    </div>
  );
}

function ListBlock({ title, items, tone = 'zinc', testId }) {
  if (!items?.length) return null;
  const toneClass =
    tone === 'amber'
      ? 'text-amber-300'
      : tone === 'red'
        ? 'text-red-300'
        : 'text-zinc-300';
  return (
    <div className="space-y-1" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{title}</p>
      <ul className={`space-y-1 text-xs ${toneClass}`}>
        {items.map((item) => (
          <li key={String(item)}>• {String(item)}</li>
        ))}
      </ul>
    </div>
  );
}

const WorkflowImportPreview = ({ preview }) => {
  if (!preview) return null;

  return (
    <div
      className="space-y-4 rounded-lg border border-zinc-700 bg-zinc-800/50 p-4"
      data-testid="workflow-import-preview"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-500">Workflow name</p>
          <h3 className="text-base font-semibold text-white" data-testid="workflow-import-preview-name">
            {preview.name || 'Untitled Workflow'}
          </h3>
          {preview.description ? (
            <p className="mt-1 text-sm text-zinc-400" data-testid="workflow-import-preview-description">
              {preview.description}
            </p>
          ) : null}
        </div>
        <Badge
          className="shrink-0 border-zinc-600 bg-zinc-700 text-zinc-200"
          data-testid="workflow-import-preview-schema"
        >
          v{preview.schema_version || '1.0'}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CountRow label="Triggers" value={preview.trigger_count} testId="preview-trigger-count" />
        <CountRow label="Steps" value={preview.step_count} testId="preview-step-count" />
        <CountRow label="Branches" value={preview.branch_count} testId="preview-branch-count" />
        <CountRow
          label="Connections"
          value={preview.connection_count}
          testId="preview-connection-count"
        />
      </div>

      <ListBlock
        title="Required integrations"
        items={preview.required_integrations}
        testId="preview-integrations"
      />
      <ListBlock
        title="Required variables"
        items={preview.required_variables}
        testId="preview-variables"
      />
      <ListBlock
        title="Required backend actions"
        items={preview.required_backend_actions}
        testId="preview-backend-actions"
      />
      <ListBlock
        title="Unsupported triggers/actions"
        items={[
          ...(preview.unsupported_triggers || []),
          ...(preview.unsupported_actions || []),
        ]}
        tone="amber"
        testId="preview-unsupported"
      />
      <ListBlock
        title="Warnings"
        items={preview.warnings}
        tone="amber"
        testId="preview-warnings"
      />
      <ListBlock
        title="Security issues"
        items={preview.security_issues}
        tone="red"
        testId="preview-security-issues"
      />

      <div className="flex items-center gap-2 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
        Import always creates a new draft workflow. Nothing will be activated.
      </div>

      {(preview.warnings?.length || preview.security_issues?.length) ? (
        <div className="flex items-start gap-2 text-xs text-amber-300">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Review warnings before importing.
        </div>
      ) : null}
    </div>
  );
};

export default WorkflowImportPreview;
